import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Wrench, Anchor, Store, ArrowRight } from "lucide-react";
import { SITE, IMAGES } from "@/data/site";

const VALUES = [
  { icon: Flame, title: "BUILT WITH PRIDE", text: "Hot rods aren't just a job here — they're the reason the doors open. Every build and repair gets the same care we'd give our own." },
  { icon: Wrench, title: "ONE WORKSHOP, EVERY TRADE", text: "Servicing, WOF, alignment, welding and fabrication under one roof means no runaround — the job gets done start to finish in-house." },
  { icon: Anchor, title: "ROAD AND WATER", text: "Coastal town, coastal life. We look after the boat and trailer as well as the tow vehicle, so the whole rig is ready for the weekend." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-white/10 bg-[#111] relative grain">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <p className="label-industrial text-ember mb-4">About Us</p>
          <h1 className="font-display text-6xl lg:text-8xl tracking-wide text-white" data-testid="about-heading">
            THE SHOP BEHIND THE SIGN
          </h1>
          <p className="mt-6 text-gray-400 max-w-xl">
            JCD Customs & Marine is Whangamata's home for hot rods, honest mechanical work and marine know-how.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="label-industrial text-ember mb-4">Our Story</p>
          <h2 className="font-display text-5xl tracking-wide text-white mb-8" data-testid="about-story-heading">
            A COROMANDEL WORKSHOP WITH HOT ROD BLOOD
          </h2>
          <div className="space-y-6 text-gray-400 text-sm leading-relaxed" data-testid="about-story-text">
            <p>
              Based at 205 Aickin Road in the heart of Whangamata, JCD Customs & Marine has built a name for doing the lot — from ground-up hot rod builds and custom fabrication to everyday servicing, WOF inspections and tyre alignments.
            </p>
            <p>
              Being a beach town, half of what rolls in floats too. We service boats, outboards and trailers alongside the cars, so your whole setup is sorted in one stop.
            </p>
            <p>
              The retail shop on-site stocks Repco quality auto parts, marine supplies and workshop consumables over the counter — the same gear we use on the floor ourselves.
            </p>
            <p>
              Whether it's a '34 coupe, the family wagon or the trailer that hasn't seen grease in years, bring it in. We'll give it to you straight and do the job right.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="about-book-btn"
            className="inline-block mt-10 label-industrial bg-ember text-white px-8 py-4 hover:bg-ember-hover hover:-translate-y-1 transition-[background-color,transform] duration-150"
          >
            Book a Job
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="space-y-4"
        >
          <div className="border border-white/10 overflow-hidden" data-testid="about-photo-main">
            <img src="/hero.jpg" alt="JCD Customs & Marine shop front with hot rods" className="w-full object-cover" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-white/10 bg-[#111] p-8" data-testid="about-badge-repco">
              <p className="label-industrial text-ember mb-2">Stockist</p>
              <p className="font-display text-3xl tracking-wide text-white">REPCO PARTS</p>
            </div>
            <div className="border border-white/10 bg-[#111] p-8" data-testid="about-badge-marine">
              <p className="label-industrial text-ember mb-2">On the water</p>
              <p className="font-display text-3xl tracking-wide text-white">MARINE READY</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Values */}
      <section className="border-y border-white/10 bg-[#111] relative grain">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <p className="label-industrial text-ember mb-4">How we work</p>
          <h2 className="font-display text-5xl tracking-wide text-white mb-16" data-testid="about-values-heading">
            WHAT WE STAND FOR
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`about-value-${i}`}
              >
                <v.icon className="w-8 h-8 text-ember mb-6" />
                <h3 className="font-display text-2xl tracking-wider text-white mb-3">{v.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visit strip */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="border border-white/10 bg-[#111] p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center gap-8" data-testid="about-visit-banner">
          <Store className="w-12 h-12 text-ember shrink-0" />
          <div className="flex-1">
            <h3 className="font-display text-4xl tracking-wide text-white">COME SEE THE SHOP</h3>
            <p className="text-gray-400 text-sm mt-3 max-w-xl">
              {SITE.location} — {SITE.hours[0].days.toLowerCase()} {SITE.hours[0].time}, Saturday {SITE.hours[1].time}. Call {SITE.phone} or flick us a message.
            </p>
          </div>
          <Link
            to="/services"
            data-testid="about-services-link"
            className="label-industrial text-ember hover:text-ember-hover transition-colors duration-150 flex items-center gap-2 shrink-0"
          >
            See what we do <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
